import { TranslateService } from '@project/translate';
import { doctorSpecializationRegistry } from './doctor-specialization-registry';

/**
 * @deps By editing keys of this enum, it is necessary to update the template files for csv import.
 */
export enum EDoctorSpecializationDTO {
  Infectologist = 'Infectologist',
  GeneralPhysician = 'GeneralPhysician',
  Pulmonologist = 'Pulmonologist',
  Cardiologist = 'Cardiologist',
  ChildCardiologist = 'ChildCardiologist',
  Intensivist = 'Intensivist',
  Dermatologist = 'Dermatologist',
  Physiotherapist = 'Physiotherapist',
  Psychologist = 'Psychologist',
  ChildPsychologist = 'ChildPsychologist',
  Psychiatrist = 'Psychiatrist',
  VascularSurgeon = 'VascularSurgeon',
  Endocrinologist = 'Endocrinologist',
  ChildEndocrinologist = 'ChildEndocrinologist',
  Gastroenterologist = 'Gastroenterologist',
  Gynecologist = 'Gynecologist',
  Obstetric = 'Obstetric',
  Hematologist = 'Hematologist',
  Nephrologist = 'Nephrologist',
  Neurologist = 'Neurologist',
  Nutritionist = 'Nutritionist',
  Ophthalmologist = 'Ophthalmologist',
  Orthopedist = 'Orthopedist',
  Otolaryngologist = 'Otolaryngologist',
  Pediatrician = 'Pediatrician',
  ChildPsychiatrist = 'ChildPsychiatrist',
  Rheumatologist = 'Rheumatologist',
  Traumatologist = 'Traumatologist',
  Urologist = 'Urologist',
  GeneralSurgeon = 'GeneralSurgeon',
  ChildNeurologist = 'ChildNeurologist',
  Geriatrician = 'Geriatrics',
  Hepatology = 'Hepatology',
  Allergology = 'Allergology',
  Gastroenterology = 'Gastroenterology',
}

function getLegacyDoctorSpecializationLocalization(specialization: EDoctorSpecializationDTO): string | undefined {
  const map: Record<EDoctorSpecializationDTO, () => string> = {
    [EDoctorSpecializationDTO.Infectologist]: () => TranslateService.localize('doctors.specialization.infectologist'),
    [EDoctorSpecializationDTO.GeneralPhysician]: () =>
      TranslateService.localize('doctors.specialization.general-physician'),
    [EDoctorSpecializationDTO.Pulmonologist]: () => TranslateService.localize('doctors.specialization.pulmonologist'),
    [EDoctorSpecializationDTO.Cardiologist]: () => TranslateService.localize('doctors.specialization.cardiologist'),
    [EDoctorSpecializationDTO.ChildCardiologist]: () =>
      TranslateService.localize('doctors.specialization.child-cardiologist'),
    [EDoctorSpecializationDTO.Intensivist]: () => TranslateService.localize('doctors.specialization.intensivist'),
    [EDoctorSpecializationDTO.Dermatologist]: () => TranslateService.localize('doctors.specialization.dermatologist'),
    [EDoctorSpecializationDTO.Physiotherapist]: () =>
      TranslateService.localize('doctors.specialization.physiotherapist'),
    [EDoctorSpecializationDTO.Psychologist]: () => TranslateService.localize('doctors.specialization.psychologist'),
    [EDoctorSpecializationDTO.ChildPsychologist]: () =>
      TranslateService.localize('doctors.specialization.child-psychologist'),
    [EDoctorSpecializationDTO.Psychiatrist]: () => TranslateService.localize('doctors.specialization.psychiatrist'),
    [EDoctorSpecializationDTO.VascularSurgeon]: () =>
      TranslateService.localize('doctors.specialization.vascular-surgeon'),
    [EDoctorSpecializationDTO.Endocrinologist]: () =>
      TranslateService.localize('doctors.specialization.endocrinologist'),
    [EDoctorSpecializationDTO.ChildEndocrinologist]: () =>
      TranslateService.localize('doctors.specialization.child-endocrinologist'),
    [EDoctorSpecializationDTO.Gastroenterologist]: () =>
      TranslateService.localize('doctors.specialization.gastroenterologist'),
    [EDoctorSpecializationDTO.Gynecologist]: () => TranslateService.localize('doctors.specialization.gynecologist'),
    [EDoctorSpecializationDTO.Obstetric]: () => TranslateService.localize('doctors.specialization.obstetric'),
    [EDoctorSpecializationDTO.Hematologist]: () => TranslateService.localize('doctors.specialization.hematologist'),
    [EDoctorSpecializationDTO.Nephrologist]: () => TranslateService.localize('doctors.specialization.nephrologist'),
    [EDoctorSpecializationDTO.Neurologist]: () => TranslateService.localize('doctors.specialization.neurologist'),
    [EDoctorSpecializationDTO.Nutritionist]: () => TranslateService.localize('doctors.specialization.nutritionist'),
    [EDoctorSpecializationDTO.Ophthalmologist]: () =>
      TranslateService.localize('doctors.specialization.ophthalmologist'),
    [EDoctorSpecializationDTO.Orthopedist]: () => TranslateService.localize('doctors.specialization.orthopedist'),
    [EDoctorSpecializationDTO.Otolaryngologist]: () =>
      TranslateService.localize('doctors.specialization.otolaryngologist'),
    [EDoctorSpecializationDTO.Pediatrician]: () => TranslateService.localize('doctors.specialization.pediatrician'),
    [EDoctorSpecializationDTO.ChildPsychiatrist]: () =>
      TranslateService.localize('doctors.specialization.child-psychiatrist'),
    [EDoctorSpecializationDTO.Rheumatologist]: () => TranslateService.localize('doctors.specialization.rheumatologist'),
    [EDoctorSpecializationDTO.Traumatologist]: () => TranslateService.localize('doctors.specialization.traumatologist'),
    [EDoctorSpecializationDTO.Urologist]: () => TranslateService.localize('doctors.specialization.urologist'),
    [EDoctorSpecializationDTO.GeneralSurgeon]: () => TranslateService.localize('doctors.specialization.generalsurgeon'),
    [EDoctorSpecializationDTO.ChildNeurologist]: () =>
      TranslateService.localize('doctors.specialization.childNeurologist'),
    [EDoctorSpecializationDTO.Geriatrician]: () => TranslateService.localize('doctors.specialization.geriatrician'),
    [EDoctorSpecializationDTO.Hepatology]: () => TranslateService.localize('doctors.specialization.hepatologist'),
    [EDoctorSpecializationDTO.Allergology]: () => TranslateService.localize('doctors.specialization.allergologist'),
    [EDoctorSpecializationDTO.Gastroenterology]: () =>
      TranslateService.localize('doctors.specialization.gastroenterologist'),
  };

  return map[specialization]?.();
}

/**
 * Codes that can be assigned to a doctor: the active remote (Firestore/cache) specializations, or the
 * local enum values when no remote list is available. Deactivated specializations are excluded.
 */
export function getSelectableDoctorSpecializationCodes(): string[] {
  const activeEntries = doctorSpecializationRegistry.getActiveEntries();

  return activeEntries ? activeEntries.map((entry) => entry.code) : Object.values(EDoctorSpecializationDTO);
}

/**
 * Enum member name of a specialization code (e.g. 'Geriatrics' -> 'Geriatrician'). Returns the code itself
 * when it is not in the local enum (specialization configured only in Firestore).
 */
export function getDoctorSpecializationEnumKey(code: string): string {
  return Object.keys(EDoctorSpecializationDTO).find((key) => EDoctorSpecializationDTO[key] === code) ?? code;
}

/**
 * Name fallback chain: configured name in the current language -> configured pt-BR name ->
 * local i18n by enum -> the code itself. Returns undefined only when no specialization is given.
 */
export function getDoctorSpecializationLocalization(specialization: string): string | undefined {
  if (!specialization) {
    return undefined;
  }

  return (
    doctorSpecializationRegistry.getName(specialization) ??
    getLegacyDoctorSpecializationLocalization(specialization as EDoctorSpecializationDTO) ??
    specialization
  );
}
